import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Loader2, Plus, Calendar, Pencil, FileText } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import StarsPanel from "@/components/StarsPanel";
import EditStaffModal from "@/components/EditStaffModal";
import StaffEvalReportModal from "@/components/StaffEvalReportModal";
import { canUserManageStaff } from "@/lib/staffPermissions";

export default function StaffDettaglio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [staff, setStaff] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get(`/staff/${id}`),
        api.get(`/staff/${id}/reviews`),
      ]);
      setStaff(s.data);
      setReviews(r.data || []);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>;
  if (!staff) return <div>Dipendente non trovato.</div>;

  const canEvaluate = canUserManageStaff(user, staff);
  const canGenerateReport = ["super_admin", "resident_manager"].includes(user?.role);

  const trendData = reviews.slice().reverse().map((r) => ({
    data: new Date(r.data_valutazione).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }),
    punteggio: r.punteggio_medio,
  }));

  return (
    <div className="space-y-6 ih-anim-in" data-testid="staff-dettaglio-page">
      <button onClick={() => navigate(-1)} className="text-sm flex items-center gap-1 text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Indietro</button>

      <div className="ih-glass rounded-2xl p-7 flex items-center gap-5">
        <div className="h-20 w-20 rounded-full bg-lavender-300 dark:bg-lavender-900/50 flex items-center justify-center text-2xl font-bold">{staff.nome[0]}{staff.cognome[0]}</div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-3xl tracking-tight font-light">{staff.nome} <span className="font-medium">{staff.cognome}</span></h1>
          <div className="text-sm text-muted-foreground mt-1">{staff.ruolo}</div>
          <div className="flex flex-wrap gap-3 mt-3 text-xs text-muted-foreground">
            {Number.isFinite(staff.eta) && <span data-testid="staff-eta">Età {staff.eta} anni</span>}
            {staff.data_assunzione && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Assunto il {new Date(staff.data_assunzione).toLocaleDateString("it-IT")}</span>}
            <span>· {reviews.length} valutazione/i</span>
          </div>
        </div>
        {canEvaluate && (
          <div className="flex items-center gap-2">
            {canGenerateReport && (
              <button
                data-testid="staff-eval-report-btn-detail"
                onClick={() => setReportOpen(true)}
                className="px-3 py-2 rounded-full border border-lavender-400 text-lavender-700 dark:text-lavender-300 dark:border-lavender-500/60 hover:bg-lavender-50 dark:hover:bg-lavender-950/30 text-sm font-medium flex items-center gap-2"
              >
                <FileText className="h-4 w-4" /> Scarica scheda PDF
              </button>
            )}
            <button
              data-testid="edit-staff-btn"
              onClick={() => setEditOpen(true)}
              className="px-3 py-2 rounded-full border border-border text-sm font-medium flex items-center gap-2 hover:bg-accent"
            >
              <Pencil className="h-4 w-4" /> Modifica
            </button>
            <button data-testid="new-review-btn" onClick={() => navigate(`/staff/${id}/nuova-valutazione`)} className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2"><Plus className="h-4 w-4" /> Nuova valutazione</button>
          </div>
        )}
      </div>

      <StaffEvalReportModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        initialStaffId={id}
      />

      <EditStaffModal
        staff={staff}
        currentUser={user}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSaved={() => { setEditOpen(false); load(); }}
      />

      <StarsPanel staff={staff} currentUser={user} />

      {trendData.length > 1 && (
        <div className="ih-glass rounded-2xl p-6">
          <h2 className="font-display text-lg tracking-tight mb-4">Storico punteggio medio</h2>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="data" fontSize={10} />
                <YAxis fontSize={10} domain={[0, 10]} />
                <Tooltip />
                <Line type="monotone" dataKey="punteggio" stroke="#c4a3e6" strokeWidth={2.5} dot={{ r: 4, fill: "#c4a3e6" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="ih-glass rounded-2xl p-6">
        <h2 className="font-display text-lg tracking-tight mb-4">Storico valutazioni</h2>
        {reviews.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nessuna valutazione registrata.</p>
        ) : (
          <div className="space-y-2">
            {reviews.map((r) => (
              <div key={r.id} onClick={() => navigate(`/staff/${id}/valutazione/${r.id}`)} data-testid={`review-${r.id}`} className="rounded-xl border border-border/60 p-4 bg-background/40 hover:bg-accent/30 cursor-pointer flex items-center gap-3 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{r.periodo} · {new Date(r.data_valutazione).toLocaleDateString("it-IT")}</div>
                  <div className="font-medium text-sm">{r.valutatore_nome}</div>
                  {r.commento_generale && <div className="text-[11px] text-muted-foreground truncate mt-0.5">{r.commento_generale}</div>}
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl">{r.punteggio_medio}</div>
                  <div className="text-[10px] text-muted-foreground">/10</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
